// @ts-nocheck

import React, { useMemo } from 'react';
import styled from 'styled-components';

import { ReactComponent as CloseIcon } from '../../../assets/svg/close.svg';
import { __ } from '../../../i18n/l10n';
import { FlatButton, SecondaryButton } from '../../../styled-components/shared';
import playerErrors from '../constants/playerErrors';
import ImmersivePlayerError from '../immersivePlayerError';
import VideoJsError from '../videoJsError';

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const CloseButton = styled(FlatButton)`
  && {
    background: #fff;
    position: absolute;
    top: 1em;
    right: 1em;
    border-radius: 50%;
    margin: 0;
    padding: 0.5em;
  }
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCloseIcon = styled(CloseIcon)`
  width: 1.2em;
  height: 1.2em;
  color: #000;
`;

type ErrorBoundaryFallbackComponentProps = {
  error: ImmersivePlayerError | VideoJsError | unknown;
  closeable: boolean;
  onClose: () => void;
  onRetry: () => void;
};
const ErrorBoundaryFallbackComponent = ({
  error,
  closeable,
  onClose,
  onRetry,
}: ErrorBoundaryFallbackComponentProps) => {
  const onCloseButtonClick = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const message = useMemo(() => {
    if (error instanceof ImmersivePlayerError) {
      switch (error.message) {
        case playerErrors.ERROR_CODE_ACCESS_DENIED:
        case playerErrors.ERROR_NOT_CONNECTED:
          return __(`You are not connected.`);
        case playerErrors.ERROR_CODE_MAINTENANCE:
          return __('Maintenance in progress.');
        case playerErrors.ERROR_CODE_CANNOT_DISPLAY:
        case playerErrors.ERROR_CANNOT_DISPLAY:
          return __('You must have a minimum of €1 on your account, or you must have placed a bet on this match.');
        case playerErrors.ERROR_CODE_STREAM_GEOBLOCKED:
        case playerErrors.ERROR_GEO_BLOCKED:
          return __('This broadcast is not available in your country of residence.');
        case playerErrors.ERROR_CODE_VIDEO_UNAVAILABLE:
        case playerErrors.ERROR_UNAVAILABLE:
          // Video indisponible
          // Geo blocked
          // Actions: Quit
          return __('Video unavailable');
        case playerErrors.ERROR_CODE_BAD_REQUEST_MISSING_PARAMS:
        case playerErrors.ERROR_CODE_BAD_REQUEST_INVALID_PARAMS:
        case playerErrors.ERROR_CODE_INVALID_REFERENCE:
        case playerErrors.ERROR_CODE_DEFAULT:
        default:
          return __('An error has occured');
      }
    }
    if (error instanceof VideoJsError) {
      // MEDIA_ERR_ABORTED: Le téléchargement de la vidéo a été interrompu.
      // MEDIA_ERR_NETWORK: La connexion a été perdue. Vérifiez votre connexion internet.
      // MEDIA_ERR_DECODE: La vidéo est de mauvaise qualité ou dans un format incompatible avec votre navigateur.
      // MEDIA_ERR_SRC_NOT_SUPPORTED: Cette vidéo est indisponible ou non prise en charge par ce navigateur.
      // Actions: Retry | Quit
      return error.message;
    }
    // Actions: Quit
    return __('An error has occured');
  }, [error]);

  // retry only if VideoJsError
  const retry = useMemo(() => error instanceof VideoJsError, [error]);

  return (
    <Wrapper>
      <div>
        <p>{message}</p>
        {retry ? <SecondaryButton onClick={onRetry}>{__('Try again')}</SecondaryButton> : null}
        {closeable && (
          <CloseButton onClick={onCloseButtonClick}>
            <Center>
              <StyledCloseIcon />
            </Center>
          </CloseButton>
        )}
      </div>
    </Wrapper>
  );
};

export default ErrorBoundaryFallbackComponent;
